import { z } from 'zod';
/**
 * DeepResearchRun + ResearchStep — the wire shape the Ask "deep
 * research mode" visible-trace surface consumes (issue #166 PR A —
 * ask extras). When the CEO toggles deep-research on Ask, the agent
 * runs an extended research loop and streams a trace of every step it
 * took: which connector it queried, what it found, how long each leg
 * took. The trace renders as a vertical timeline with bold step titles
 * + muted body + mono ts caption.
 *
 * Persisted at `organizations/{orgId}/deepResearchRuns/{id}`; surfaced
 * by `POST /api/orgs/:o/ask/deep-research`.
 */
/** Per-step state — drives the icon + animation in the trace timeline. */
export const ResearchStepState = z.enum(['done', 'running', 'pending']);
/**
 * One row in the deep-research trace. `ts` is a "0:04" relative caption
 * (mono), not an ISO datetime — the design surfaces "how far in" each
 * step was, not wall-clock time. `source` is a free-text " · "-joined
 * list of connectors hit ("Salesforce · Drive · Notion") that renders
 * as the eyebrow under the step.
 */
export const ResearchStep = z.object({
    ts: z.string(),
    state: ResearchStepState,
    /** Bold step name — "Pulling Q1 board deck", "Cross-referencing Notion design doc". */
    title: z.string(),
    /** Muted body — what the agent found / why this step matters. */
    body: z.string(),
    /** " · "-joined connector list — "Salesforce · Drive". */
    source: z.string(),
});
/** Depth knob — shallow == 3-5 steps, deep == 8-15 steps. */
export const DeepResearchDepth = z.enum(['shallow', 'deep']);
export const DeepResearchRun = z.object({
    id: z.string(),
    orgId: z.string(),
    /** The CEO's question, verbatim. */
    question: z.string(),
    depth: DeepResearchDepth,
    startedAt: z.string().datetime(),
    /** Wall-clock duration so far (ms). */
    elapsedMs: z.number().int().nonnegative(),
    /** LLM tokens consumed across all steps. */
    tokensUsed: z.number().int().nonnegative(),
    /** Distinct source documents pulled. */
    sourcesRead: z.number().int().nonnegative(),
    /** The trace, newest step last. */
    steps: z.array(ResearchStep),
});
